import React from 'react';
import styles from './PropertiesPanel.module.css';
import usePropertiesModel from '../../hooks/usePropertiesModel';
import { duplicateSelectedElements } from '../../utils/elementPatch';
import { normalizeElements } from '../../utils/elementDefaults';
import CommonActions from './sections/CommonActions';
import TransformSection from './sections/TransformSection';
import ArrangeSection from './sections/ArrangeSection';
import TextEditSection from './sections/TextEditSection';
import TextStyleSection from './sections/TextStyleSection';
import TextSpacingSection from './sections/TextSpacingSection';
import ImageStyleSection from './sections/ImageStyleSection';
import ShapeStyleSection from './sections/ShapeStyleSection';
import FrameStyleSection from './sections/FrameStyleSection';
import ImageToolsController from '../../properties/ImageToolsController';
import PageSettingsSection from './sections/PageSettingsSection';

const PropertiesPanel = ({
  elements,
  selectedIds,
  setSelectedIds,
  setElementsLocal,
  commitElementsToFirestore,
  isInteractingRef,
  onStartTextEdit,
  editingTextId,
  artboardWidth,
  artboardHeight,
  brandColors,
  palette,
  photoColors,
  onUsePaint,
  onRequestEyedropper,
  imageToolPanel,
  onOpenImageTool,
  onCloseImageTool,
  onApplyImageCrop,
  onCancelImageCrop,
  onResetImageCrop,
  onClearImageEraser,
  pageSettings,
  onUpdatePageSettings,
  frameCropState,
  onStartFrameCrop,
  onCommitFrameCrop,
  onCancelFrameCrop,
  onResetFrameCrop,
  onFrameContentScaleChange,
  onDebugFillFrame,
  debugMode,
  assets,
  assetsLoading,
  onSelectFrameAsset,
  onSaveAsUpload,
  onSelectBackgroundImage,
  onClearBackgroundImage,
  onToggleBackgroundReposition,
  isBackgroundRepositioning,
  onCommitBackgroundReposition,
  onCancelBackgroundReposition,
  onResetBackgroundReposition,
  onPreviewPageSettings,
}) => {
  const normalizedElements = normalizeElements(elements);

  const model = usePropertiesModel({
    elements: normalizedElements,
    selectedIds,
    setElementsLocal,
    commitElementsToFirestore,
    isInteractingRef,
  });

  const handleDelete = () => {
    if (!selectedIds.length) return;
    const next = normalizedElements.filter((el) => !selectedIds.includes(el.id));
    setElementsLocal(next);
    commitElementsToFirestore(next, 'delete');
    setSelectedIds([]);
  };

  const handleDuplicate = () => {
    if (!selectedIds.length) return;
    const { elements: nextElements, newIds } = duplicateSelectedElements(
      normalizedElements,
      selectedIds,
      10,
    );
    setElementsLocal(nextElements);
    commitElementsToFirestore(nextElements, 'duplicate');
    setSelectedIds(newIds);
  };

  const selectionLabel = model.selectionType === 'text'
    ? 'Text'
    : model.selectionType === 'image'
      ? 'Image'
      : model.selectionType === 'shape'
        ? 'Shape'
        : model.selectionType === 'frame'
          ? 'Frame'
        : model.selectionType === 'mixed'
        ? 'Multiple'
        : 'Select an element';
  const disableEditing = !model.canEdit;
  const canSaveAsUpload = selectedIds.length === 1;

  if (model.selectionType === 'none') {
    return (
      <div className={styles.panel}>
      <div className={styles.header}>
        <span>Page</span>
      </div>
        <PageSettingsSection
          pageSettings={pageSettings}
          onUpdate={onUpdatePageSettings}
          onPreviewUpdate={onPreviewPageSettings}
          brandColors={brandColors}
          palette={palette}
          onUsePaint={onUsePaint}
          onRequestEyedropper={onRequestEyedropper}
          assets={assets}
          assetsLoading={assetsLoading}
          onSelectBackgroundImage={onSelectBackgroundImage}
          onClearBackgroundImage={onClearBackgroundImage}
          onToggleBackgroundReposition={onToggleBackgroundReposition}
          isBackgroundRepositioning={isBackgroundRepositioning}
          onCommitBackgroundReposition={onCommitBackgroundReposition}
          onCancelBackgroundReposition={onCancelBackgroundReposition}
          onResetBackgroundReposition={onResetBackgroundReposition}
          artboardWidth={artboardWidth}
          artboardHeight={artboardHeight}
        />
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span>{selectionLabel}</span>
      </div>

      <CommonActions
        model={model}
        onDelete={handleDelete}
        onDuplicate={handleDuplicate}
        onSaveAsUpload={onSaveAsUpload}
        canSaveAsUpload={canSaveAsUpload}
      />

      {model.selectionType === 'text' && (
        <>
          <TextEditSection
            model={model}
            selectedIds={selectedIds}
            onStartTextEdit={onStartTextEdit}
            editingTextId={editingTextId}
          />
          <TextStyleSection
            model={model}
            brandColors={brandColors}
            palette={palette}
            onUsePaint={onUsePaint}
            onRequestEyedropper={onRequestEyedropper}
            disabled={disableEditing}
          />
          <TextSpacingSection model={model} disabled={disableEditing} />
        </>
      )}

      {model.selectionType === 'shape' && (
        <ShapeStyleSection
          model={model}
          palette={palette}
          brandColors={brandColors}
          onUsePaint={onUsePaint}
          onRequestEyedropper={onRequestEyedropper}
          disabled={disableEditing}
        />
      )}

      {model.selectionType === 'frame' && (
        <FrameStyleSection
          model={model}
          palette={palette}
          brandColors={brandColors}
          onUsePaint={onUsePaint}
          onRequestEyedropper={onRequestEyedropper}
          disabled={disableEditing}
          frameCropState={frameCropState}
          onStartFrameCrop={onStartFrameCrop}
          onCommitFrameCrop={onCommitFrameCrop}
          onCancelFrameCrop={onCancelFrameCrop}
          onResetFrameCrop={onResetFrameCrop}
          onFrameContentScaleChange={onFrameContentScaleChange}
          onDebugFillFrame={onDebugFillFrame}
          debugMode={debugMode}
          assets={assets}
          assetsLoading={assetsLoading}
          onSelectFrameAsset={onSelectFrameAsset}
        />
      )}

      {model.selectionType === 'image' && (
        <>
          <ImageToolsController
            model={model}
            disabled={disableEditing}
            toolState={imageToolPanel}
            onOpenTool={onOpenImageTool}
            onCloseTool={onCloseImageTool}
            onApplyCrop={onApplyImageCrop}
            onCancelCrop={onCancelImageCrop}
            onResetCrop={onResetImageCrop}
            onClearEraser={onClearImageEraser}
          />
          <ImageStyleSection
            model={model}
            palette={palette}
            onUsePaint={onUsePaint}
            onRequestEyedropper={onRequestEyedropper}
            photoColors={photoColors}
            disabled={disableEditing}
          />
        </>
      )}

      <TransformSection model={model} disabled={disableEditing} />
      <ArrangeSection
        model={model}
        elements={normalizedElements}
        selectedIds={selectedIds}
        setElementsLocal={setElementsLocal}
        commitElementsToFirestore={commitElementsToFirestore}
        artboardWidth={artboardWidth}
        artboardHeight={artboardHeight}
      />
    </div>
  );
};

export default PropertiesPanel;
